import { fakeZoom, stickySize } from "@/model/Settings";
import type { ZoomedRectangle } from "@/model/board";
import type {
  AnyCoordinate,
  BoardCoordinate,
  RelativeCoordinate,
  ViewportCoordinate,
  WindowCoordinate,
  WindowLen,
} from "@/model/coordinates";
import { initWindowCoord, relativeCoord } from "@/model/coordinates";
import { cardSize, halfCardSize, useBoardStore } from "@/store/board";
import { useBoardSizeStore } from "@/store/boardSize";
import { useZoomStore } from "@/store/zoom";

import { clampCoord, divided, minus, plus, times } from "./coordinates";

/**
 * Convert a window coordinate into a board coordinate.
 * @param c The window coordinate to be converted.
 * @param limitCoords If the coordinate should be checked and adjusted if needed
 *    - "input": Check the window coordinate parameter to be inside the window
 *    - "output": Check the resulting board coordinate to be inside the board
 * @param additionalOffsets A list of additional offsets to the given coordinate that should also be checked.
 */
export function boardFromWindow(
  c: WindowCoordinate,
  limitCoords: "output" | "none" = "output",
  additionalOffsets: Array<{ offset: WindowLen }> = [],
): BoardCoordinate {
  const boardRect = useBoardSizeStore().board;
  const zoom = useZoomStore().factor;
  const halfCard = windowScaleFromRelative(halfCardSize());
  const limited = limit(c);
  for (const additional of additionalOffsets) {
    combineDeltas(limited, limit(plus(c, times(additional.offset, zoom))));
  }
  return boardScaleFromWindow(plus(limited, limited.delta));

  function limit(inp: WindowCoordinate) {
    const c = minus(divided(minus(inp, boardRect.p0), zoom), halfCard);
    return { ...c, delta: limitCoords === "output" ? minus(insideBoard(c), c) : initWindowCoord() };
  }

  function combineDeltas(
    target: { delta: WindowCoordinate },
    combine: { delta: WindowCoordinate },
  ) {
    if (Math.abs(combine.delta.x) > Math.abs(target.delta.x)) {
      target.delta.x = combine.delta.x;
    }
    if (Math.abs(combine.delta.y) > Math.abs(target.delta.y)) {
      target.delta.y = combine.delta.y;
    }
  }

  function insideBoard(c: WindowCoordinate): WindowCoordinate {
    return clampCoord(c, initWindowCoord(), maxWindowCoord());
  }
}

interface BoardRect {
  board: ZoomedRectangle;
}

interface BoardSize {
  board: BoardCoordinate;
}

interface BoardLen {
  board: WindowLen;
}

interface CardSize {
  card: RelativeCoordinate;
}

export function windowFromBoard(c: BoardCoordinate, size?: BoardRect): WindowCoordinate {
  const boardRect = size?.board || { ...useBoardSizeStore().board, zoom: useZoomStore().factor };
  const offset = windowScaleFromRelative(halfCardSize(), { board: boardRect.size });
  return cast(plus(times(plus(windowScaleFromBoard(c), offset), boardRect.zoom), boardRect.p0));
}

export function relativeFromWindow(c: WindowCoordinate, size?: BoardRect): RelativeCoordinate {
  const boardRect = size?.board || { ...useBoardSizeStore().board, zoom: useZoomStore().factor };
  const base = minus(c, boardRect.p0);
  return relativeScaleFromWindow(base, { board: times(boardRect.size, boardRect.zoom) });
}

export function windowFromRelative(c: RelativeCoordinate, size?: BoardRect): WindowCoordinate {
  const boardRect = size?.board || { ...useBoardSizeStore().board, zoom: useZoomStore().factor };
  const scaled = windowScaleFromRelative(c, { board: boardRect.size });
  return cast(plus(times(scaled, boardRect.zoom), boardRect.p0));
}

/**
 * Converts a board coordinate for the top-left corner of the card to
 * a relative coordinate for the center of the card
 *
 * @param c The board coordinate for the top-left corner of the card
 * @returns The relative coordinate for the center of the card
 */
export function relativeFromBoard(c: BoardCoordinate, size?: CardSize): RelativeCoordinate {
  const cardSize = size?.card || useBoardStore().currentBoard().cardSize;
  return plus(relativeScaleFromBoard(c), divided(cardSize, 2));
}

export function boardFromRelative(
  c: RelativeCoordinate,
  size?: BoardSize & CardSize,
): BoardCoordinate {
  const halfCard = divided(size?.card || useBoardStore().currentBoard().cardSize, 2);
  const board = size?.board || boardScaleFromWindow(useBoardSizeStore().board.size);
  return boardScaleFromRelative(minus(c, halfCard), { board });
}

/**
 * Converts a relative distance from the center of a card into a coordinate
 * for the top-left corner of this card.
 * This is a bit different from the rest of the functions here.
 */
export function cardFromRelative(c: RelativeCoordinate): WindowCoordinate {
  return cast(times(plus(divided(c, cardSize()), relativeCoord(0.5, 0.5)), stickySize));
}

export function boardScaleFromRelative(c: RelativeCoordinate, size?: BoardSize): BoardCoordinate {
  const boardSize = size?.board || boardScaleFromWindow(useBoardSizeStore().board.size);
  return times(cast(c), boardSize);
}

export function relativeScaleFromBoard(c: BoardCoordinate): RelativeCoordinate {
  const boardSize = boardScaleFromWindow(useBoardSizeStore().board.size);
  return cast(divided(c, boardSize));
}

function relativeScaleFromWindow(
  c: WindowCoordinate | WindowLen,
  size?: BoardLen,
): RelativeCoordinate {
  const boardSize = size?.board || useBoardSizeStore().board.size;
  return cast(divided(cast(c), boardSize));
}

export function windowScaleFromRelative(c: RelativeCoordinate, size?: BoardLen): WindowLen {
  const boardSize = size?.board || useBoardSizeStore().board.size;
  return cast(times(cast(c), boardSize));
}

export function windowScaleFromBoard(c: BoardCoordinate): WindowLen {
  return cast(divided(c, fakeZoom));
}

export function boardScaleFromWindow(c: WindowCoordinate | WindowLen): BoardCoordinate {
  return cast(times(c, fakeZoom));
}

function maxWindowCoord(): WindowCoordinate {
  return cast(
    windowScaleFromRelative(minus(relativeCoord(1, 1), useBoardStore().currentBoard().cardSize)),
  );
}

// the difference between a window and the "same" viewport coordinate is the scroll
export function scrollDiff(w: WindowCoordinate, v: ViewportCoordinate): WindowLen {
  return cast(minus(w, cast(v)));
}

// use only exceptionally when a coordinate really represents a length
// e.g. asLen(windowFromRelative(<a relative coord representing a length>)) because there's no RelativeLen
// or asLen(minus(coord1, coord2))
export function asLen(c: WindowCoordinate | ViewportCoordinate): WindowLen {
  return cast(c);
}

function cast<T extends AnyCoordinate>(c: AnyCoordinate): T {
  return c as T;
}
